import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, CalendarHeart, Megaphone, ClipboardList, BadgeCheck, Wrench, ArrowRight, Wallet, Clock, TrendingUp, Award, Gift, Zap, TrendingDown, Map, Users } from 'lucide-react';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1144" data-mp="3j2tux1gunas3">
    {children}
  </motion.div>;
export function TropaDoBairrou() {
  const t = useT();
  const opportunityIcons = [<Truck size={28} data-id="element-1145" data-mp="189pq5o70vqa3" />, <CalendarHeart size={28} data-id="element-1146" data-mp="1897m45e4c7rm" />, <Megaphone size={28} data-id="element-1147" data-mp="18clld4rhk0uu" />, <ClipboardList size={28} data-id="element-1148" data-mp="2foxz696kvoji" />, <BadgeCheck size={28} data-id="element-1149" data-mp="2fnwc16cafblf" />, <Wrench size={28} data-id="element-1150" data-mp="2fm3fwkzaw9ro" />];
  const participantBenefitIcons = [<Wallet size={28} data-id="element-1151" data-mp="2bo5pjetsnhmp" />, <Clock size={28} data-id="element-1152" data-mp="2bkq845cndwi4" />, <TrendingUp size={28} data-id="element-1153" data-mp="26nrf5q4pzvli" />, <Award size={28} data-id="element-1154" data-mp="26myhrfrniig1" />, <Gift size={28} data-id="element-1155" data-mp="26m6km0ieocnn" />];
  const businessBenefitIcons = [<Zap size={28} data-id="element-1156" data-mp="21p6ejplk39wj" />, <TrendingDown size={28} data-id="element-1157" data-mp="21nlmc6mps31u" />, <Map size={28} data-id="element-1158" data-mp="21oerj360ykhv" />, <Users size={28} data-id="element-1159" data-mp="1wpk1bg5q9tnq" />];
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-1160" data-mp="1wqlpmmbr8cne">
      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto relative z-10" data-id="element-1161" data-mp="1rx0r8t691gno">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1162" data-mp="1nv5odk8o6v7s">
          <FadeIn data-id="element-1163" data-mp="1iyyj7to3lnqw">
            <span className="inline-block text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6" data-id="element-1164" data-mp="1izqd9021rdf2">
              {t.tropa.heroEyebrow}
            </span>
            <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight" data-id="element-1165" data-mp="191ao32mkye87">
              {t.tropa.heroTitle}
            </h1>
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-6 leading-relaxed" data-id="element-1166" data-mp="14c9hrmaad0hv">
              {t.tropa.heroDesc1}
            </p>
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-10 leading-relaxed" data-id="element-1167" data-mp="ud0rb3emq409">
              {t.tropa.heroDesc2}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1168" data-mp="1i3myksgt7k97">
              <span className="bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-8 py-4 rounded-full text-sm font-bold tracking-wide uppercase text-center cursor-default select-none border border-brand-green/20 dark:border-brand-green/30" data-id="element-1169" data-mp="1n0mwuh7f3ipr">
                {t.common.disponivelEmBreve}
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Como funciona */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-1170" data-mp="25ua7nh2gofxc">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1171" data-mp="3lk1fykgnloxx">
          <FadeIn data-id="element-1172" data-mp="3lj8enz0grrn6">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 tracking-tight" data-id="element-1173" data-mp="3lhoy07awbekv">
              {t.tropa.howItWorksTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-6 leading-relaxed" data-id="element-1174" data-mp="3vact8xm89ahh">
              {t.tropa.howItWorksDesc1}
            </p>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-1175" data-mp="32lp76ztnmh2v">
              {t.tropa.howItWorksDesc2}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Tipos de oportunidades */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-1176" data-mp="3chii6erq62ij">
        <FadeIn className="text-center mb-16" data-id="element-1177" data-mp="wqw8dm704mxt">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1178" data-mp="wts6baiunbm9">
            {t.tropa.opportunitiesTitle}
          </h2>
        </FadeIn>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1179" data-mp="2xjhjg8hxte6g">
          {t.tropa.opportunitiesTitles.map((title, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-1180" data-mp="36hbg5kjm69u5" data-mpi={i}>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 h-full hover:shadow-md transition-shadow" data-id="element-1181" data-mp="36gi4c55ssu55">
                <div className="w-14 h-14 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light rounded-2xl flex items-center justify-center mb-6" data-id="element-1182" data-mp="3gexx0ckh4bgr">
                  {opportunityIcons[i]}
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1183" data-mp="3q7wuyl1v1ed1">
                  {title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-1184" data-mp="31mdsp2pxh05">
                  {t.tropa.opportunitiesDescs[i]}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* Benefícios para quem participa */}
      <section className="bg-brand-charcoal dark:bg-gray-950 text-white py-24 px-6 relative overflow-hidden transition-colors duration-300" data-id="element-1185" data-mp="d5awi36ycsbk">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-1186" data-mp="86q06eutmsse"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1187" data-mp="10u3zukpwm2g7">
          <FadeIn className="text-center mb-16" data-id="element-1188" data-mp="vv0t8vu9zo64">
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold mb-6 tracking-tight" data-id="element-1189" data-mp="vwarpm51iyda">
              {t.tropa.participantBenefitsTitle}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-1190" data-mp="n2foijnhbi0a">
            {t.tropa.participantBenefitsTitles.map((title, i) => <FadeIn key={i} delay={i * 0.1} className={i === 4 ? 'md:col-span-2 lg:col-span-1' : ''} data-id="element-1191" data-mp="1eun3zgr90od3" data-mpi={i}>
                <div className="bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-sm h-full hover:bg-white/10 transition-colors" data-id="element-1192" data-mp="3930llp2mwkki">
                  <div className="text-brand-yellow mb-6" data-id="element-1193" data-mp="2zcz27al87qbx">
                    {participantBenefitIcons[i]}
                  </div>
                  <h3 className="text-xl font-bold mb-3" data-id="element-1194" data-mp="34a7h92b2ve8y">{title}</h3>
                  <p className="text-white/70 leading-relaxed" data-id="element-1195" data-mp="2qb7slunkezcs">
                    {t.tropa.participantBenefitsDescs[i]}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Benefícios para negócios e organizações */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-1196" data-mp="2lc4s8nij5hb5">
        <FadeIn className="text-center mb-16" data-id="element-1197" data-mp="em6x8rv0y64d">
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1198" data-mp="eni08pgnb9uf">
            {t.tropa.businessBenefitsTitle}
          </h2>
        </FadeIn>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-1199" data-mp="2443uew469gfx">
          {t.tropa.businessBenefitsTitles.map((title, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-1200" data-mp="1z62ak8yx9d3e" data-mpi={i}>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 h-full hover:shadow-md transition-shadow flex gap-6 items-start" data-id="element-1201" data-mp="2dtnc3yg5f497">
                <div className="w-14 h-14 bg-brand-yellow/10 dark:bg-brand-yellow/20 text-brand-yellow-dark dark:text-brand-yellow rounded-2xl flex items-center justify-center shrink-0" data-id="element-1202" data-mp="2mtjvplnolcxe">
                  {businessBenefitIcons[i]}
                </div>
                <div data-id="element-1203" data-mp="2wrhkca9gdbqj">
                  <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1204" data-mp="2wpn7zkc8yry2">
                    {title}
                  </h3>
                  <p className="text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-1205" data-mp="12d6v40oy4fk8">
                    {t.tropa.businessBenefitsDescs[i]}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* Closing / Novo Modelo */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300" data-id="element-1206" data-mp="gjre3jquji64">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1207" data-mp="lcu81szjctmu">
          <FadeIn data-id="element-1208" data-mp="qacjpr4xtmyj">
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 mb-6 leading-relaxed" data-id="element-1209" data-mp="qavsah8p6yzw">
              {t.tropa.closingDesc1}
            </p>
            <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 mb-12 leading-relaxed" data-id="element-1210" data-mp="3oucdjtru5ok3">
              {t.tropa.closingDesc2}
            </p>
            <p className="text-3xl md:text-4xl font-display text-brand-green-dark dark:text-brand-green-light transform -rotate-1 leading-relaxed" data-id="element-1211" data-mp="2k03lszjgjwv">
              {t.tropa.closingHighlight}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 text-center max-w-4xl mx-auto relative z-10" data-id="element-1212" data-mp="1iyrx698f7x7r">
        <FadeIn data-id="element-1213" data-mp="33hc55m385odk">
          <h2 className="text-5xl md:text-6xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 tracking-tight" data-id="element-1214" data-mp="33htzuea8nujd">
            {t.tropa.ctaTitle}
          </h2>
          <p className="text-xl text-brand-charcoal/60 dark:text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-1215" data-mp="2trixgw67dtel">
            {t.tropa.ctaDesc}
          </p>
          <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-10 py-5 rounded-full text-lg font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 group" data-id="element-1216" data-mp="2orwj2jen2747">
            {t.tropa.ctaButton}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-1217" data-mp="2aur57x8oo3ye" />
          </Link>
        </FadeIn>
      </section>
    </div>;
}